// Configuration.
//
// Il n'y a plus de seuil de pertinence à régler ici depuis le 2026-08-21 : le
// rapprochement se décide dans les catégories, sur chaque fiche. Ce qui reste
// est le seuil d'AFFICHAGE de la pile, qui ne décide de rien qui se calcule ou
// se facture, seulement de ce qu'on nous demande de trancher le matin.

import { useEffect, useRef, useState } from 'react'
import { Save, Tags, Gauge, FileText, PenLine, Mail, Search, UserRound, Check } from 'lucide-react'
import { parseQuery } from '../lib/router'
import { get, post } from '../lib/api'
import { ChoixAvance, ChoixExclusions, ChoixMails, ChoixRegions, ChoixAdresse, ChoixRythme, type ChangementMails, type EtatRythme, type Exclusions, type Region, type Rythme } from '../components/rythme'
import { useData, Loading, Empty, ActionButton, useToast, PastilleTitre } from '../components/common'
import { useMots } from '../lib/vocabulaire'
import { useT } from '../lib/i18n'
import { useProfilUnique } from '../lib/formule'
import { jourCourt, parseDate } from '../lib/format'
import { ChangerMotDePasseVolontaire, SupprimerCompte, TelechargerDonnees } from '../components/monCompte'

interface Settings {
  minScore: number
  optimization: string
  tone: string
  length: string
  language: string
  questions: string
  template: string
  author: string
}

interface PilePreview {
  threshold: number
  pileTotal: number
  pileRetained: number
}

export function Config() {
  const initial = useData(() => get<Settings>('/api/settings'), [])
  const [form, setForm] = useState<Settings | null>(null)
  const [pile, setPile] = useState<PilePreview | null>(null)
  const mots = useMots()
  const t = useT()
  const toast = useToast()
  // En Radar, la signature du journal est l'adresse du compte : rien à régler.
  const profilUnique = useProfilUnique()
  const [enregistre, marquer] = useEnregistre()

  // Radar n'a plus ATS : « Sobre » en noir en tient la place (issue #235).
  useEffect(() => {
    if (initial.data && !form) setForm({ ...initial.data, template: profilUnique && initial.data.template === 'ATS-Optimized' ? 'Moderne' : initial.data.template })
  }, [initial.data])

  useEffect(() => {
    if (!form) return
    const timer = setTimeout(() => {
      get<PilePreview>(`/api/settings/pile-preview?value=${form.minScore}`)
        .then(setPile)
        .catch(() => setPile(null))
    }, 250)
    return () => clearTimeout(timer)
  }, [form?.minScore])

  if (initial.loading || !form) return <Loading label={t('config.loading')} />
  if (initial.error) return <Empty>{initial.error}</Empty>

  const set = <K extends keyof Settings>(k: K, v: Settings[K]) => setForm({ ...form, [k]: v })
  // En Radar, tout le reste de la page s'enregistre au clic : le bouton
  // « Enregistrer » n'y servait qu'aux trois listes du CV adapté, et qui
  // changeait le ton sans le voir perdait son choix. Ces listes s'enregistrent
  // donc au changement, et le bouton n'existe plus qu'en console.
  const choisir = <K extends keyof Settings>(k: K, v: Settings[K]) => {
    const nouveau = { ...form, [k]: v }
    setForm(nouveau)
    if (!profilUnique) return
    post('/api/settings', nouveau).then(
      () => marquer('cv'),
      (err: any) => toast(err?.message ?? String(err), 'err')
    )
  }

  // Quatre pastilles plutôt que quatre listes déroulantes (issue #174) : on
  // voit les choix sans ouvrir, et une ligne dit l'effet de celui qui est pris.
  const groupe = <K extends 'optimization' | 'tone' | 'length' | 'language' | 'questions' | 'template'>(
    k: K,
    libelle: string,
    choix: readonly { v: string; nom: string; effet: string }[],
    aide?: string
  ) => {
    const pris = choix.find((c) => c.v === form[k])
    return (
      <div className="cfg-sous">
        <b>{libelle}</b>
        {aide && <p className="field-help">{aide}</p>}
        <div className="regions" role="radiogroup" aria-label={libelle}>
          {choix.map((c) => (
            <button
              key={c.v}
              type="button"
              role="radio"
              aria-checked={form[k] === c.v}
              className={`region${form[k] === c.v ? ' on' : ''}`}
              onClick={() => form[k] !== c.v && choisir(k, c.v as Settings[K])}
            >
              {form[k] === c.v && <Check size={12} strokeWidth={3} />}
              {c.nom}
            </button>
          ))}
        </div>
        {pris && <p className="field-help">{pris.effet}</p>}
      </div>
    )
  }

  const sectionCv = (
    <section className="panel">
      <h4><PastilleTitre h={280}><FileText size={16} /></PastilleTitre>{mots('config.dossiers')}<Enregistre on={enregistre === 'cv'} /></h4>
      {/* La mise en page en tête (Radar, issue #235) : le modèle des prochains
          CV. La couleur se choisit dans la fenêtre du CV, avec « Toujours
          utiliser ce modèle ». */}
      {profilUnique && groupe('template', t('config.modeleLabel'), [
        { v: 'Moderne', nom: t('modeles.sobre'), effet: t('config.modele.sobreEffet') },
        { v: 'Dates', nom: t('modeles.dates'), effet: t('config.modele.datesEffet') },
        { v: 'Colonnes', nom: t('modeles.colonnes'), effet: t('modeles.colonnes.note') },
        { v: 'Compact', nom: t('modeles.compact'), effet: t('config.modele.compactEffet') },
        { v: 'Perso', nom: t('modeles.perso'), effet: t('config.modele.persoEffet') },
      ])}
      {groupe('language', t('config.languageLabel'), [
        { v: 'Automatique', nom: t('config.langue.auto'), effet: t('config.langue.autoEffet') },
        { v: 'Français', nom: t('config.langue.fr'), effet: t('config.langue.frEffet') },
        { v: 'Anglais', nom: t('config.langue.en'), effet: t('config.langue.enEffet') },
      ])}
      {groupe('optimization', t('config.optimizationLabel'), [
        { v: 'Fidèle', nom: t('config.niveau.fidele'), effet: t('config.niveau.fideleEffet') },
        { v: 'Équilibré', nom: t('config.optimizationBalanced'), effet: t('config.niveau.equilibreEffet') },
        { v: 'Ciblé', nom: t('config.optimizationTargeted'), effet: t('config.niveau.cibleEffet') },
        { v: 'Audacieux', nom: t('config.niveau.audacieux'), effet: t('config.niveau.audacieuxEffet') },
      ])}
      {/* L'avertissement ne sert qu'à qui a quitté « Fidèle » : affiché à tous,
          il se lisait comme un reproche à qui n'avait rien changé. */}
      {form.optimization !== 'Fidèle' && <p className="cfg-explain avertissement">{mots('config.fideleAide')}</p>}
      {groupe('tone', t('config.toneLabel'), [
        { v: 'Sobre', nom: t('config.toneSober'), effet: t('config.ton.sobreEffet') },
        { v: 'Dynamique', nom: t('config.toneDynamic'), effet: t('config.ton.dynamiqueEffet') },
        { v: 'Exécutif', nom: t('config.toneExecutive'), effet: t('config.ton.executifEffet') },
      ])}
      {groupe('length', t('config.lengthLabel'), [
        { v: '1 page', nom: t('config.length1Page'), effet: t('config.longueur.unePageEffet') },
        { v: '2 pages', nom: t('config.length2Pages'), effet: t('config.longueur.deuxPagesEffet') },
        { v: 'Sans limite', nom: t('config.lengthUnlimited'), effet: t('config.longueur.sansLimiteEffet') },
      ])}
      {groupe('questions', t('config.questionsLabel'), [
        { v: 'une fois', nom: t('config.questionsOnce'), effet: t('config.questions.uneFoisEffet') },
        { v: 'toujours', nom: t('config.questionsAlways'), effet: t('config.questions.toujoursEffet') },
      ], t('config.questions.aide'))}
    </section>
  )

  return (
    <>
      <div className="bar">
        <div>
          <h3>{profilUnique ? t('reglages.titre') : t('nav.config')}</h3>
          <div className="sub">{mots('config.sousTitre')}</div>
        </div>
        <div className="grow" />
        {!profilUnique && <ActionButton
          variant="primary"
          busyLabel={t('config.saving')}
          onClick={async () => {
            await post('/api/settings', form)
            toast(t('config.savedToast'))
          }}
        >
          <Save size={15} />{t('config.save')}
        </ActionButton>}
      </div>

      <div className="body cfg">
        {/* En Radar, ce bloc expliquait la classification européenne des
            métiers et « l'absence de plafond » à quelqu'un qui n'a rien à y
            régler : ses métiers se corrigent sur son profil (issue #77). */}
        {!profilUnique && (
        <section className="panel cfg-hero">
          <h4><PastilleTitre h={150}><Tags size={16} /></PastilleTitre>{mots('config.rapprochement')}</h4>
          {/* CE PANNEAU PILOTAIT UN SEUIL, jusqu'au 2026-08-21. On y réglait
              « 1,5 écart-type au-dessus de la proximité moyenne », avec un
              schéma et un aperçu chiffré pour rendre la chose compréhensible.
              C'était beaucoup d'efforts pour un réglage que personne ne pouvait
              arbitrer honnêtement. Il ne reste rien à régler ici : le
              rapprochement se décide dans les CATÉGORIES, sur chaque fiche, où
              il se voit et se corrige. */}
          <p className="cfg-explain">{mots('config.rapprochementA')}</p>
          <p className="cfg-explain">{mots('config.rapprochementB')}</p>
        </section>
        )}

        {/* Les catégories décident de ce qu'on ÉVALUE, ce curseur de ce qu'on
            nous DEMANDE DE TRANCHER. Ils sont voisins à l'écran parce qu'on les
            confond facilement, et le texte insiste sur ce qui les sépare : les
            premières travaillent avant l'IA, le second sur son jugement après.
            Ce paragraphe a parlé pendant un temps d'un « seuil ci-dessus » qui
            n'existait plus, le panneau du dessus ayant perdu son curseur : il
            envoyait le lecteur chercher un réglage absent. */}
        {/* Radar : quatre parties, dans l'ordre où l'on se pose les questions
            (issue #173). Tout était rangé sous « Vos mails », alors que les
            régions et les critères filtrent AUSSI l'accueil. */}
        {profilUnique && <ReglagesRadar cv={sectionCv} enregistre={enregistre} marquer={marquer} />}

        {!profilUnique && (
        <section className="panel">
          <h4><PastilleTitre h={215}><Gauge size={16} /></PastilleTitre>{mots('config.seuil')}</h4>
          <p className="cfg-explain">{mots('config.seuilTexte')}</p>

          <div className="cfg-slider">
            <input
              type="range"
              min={0}
              max={90}
              step={5}
              value={form.minScore}
              onChange={(e) => set('minScore', Number(e.target.value))}
              aria-label={t('config.sliderAria')}
            />
            <span className="cfg-value">{form.minScore} / 100</span>
          </div>
          <div className="cfg-scale">
            <span>{t('config.scaleLow')}</span>
            <span>{t('config.scaleHigh')}</span>
          </div>

          <div className="cfg-effect">
            {pile ? (
              <>
                <div className="eff">
                  <div className="eff-val">{pile.pileRetained}<small>/{pile.pileTotal}</small></div>
                  <div className="eff-lab">{mots('config.effetRetenues')}</div>
                </div>
                <div className="eff">
                  <div className="eff-val">{pile.pileTotal - pile.pileRetained}</div>
                  <div className="eff-lab">{mots('config.effetEcartees')}</div>
                </div>
              </>
            ) : (
              <div className="loading"><span className="spinner" />{t('config.computingEffect')}</div>
            )}
          </div>

          <p className="cfg-note">
            {mots('config.seuilNote')} {t('config.thresholdReference')}
          </p>
        </section>
        )}

        {!profilUnique && sectionCv}

        {!profilUnique && <section className="panel">
          <h4><PastilleTitre h={40}><PenLine size={16} /></PastilleTitre>{t('config.signature')}</h4>
          <div className="field">
            <label className="field-label">{t('config.authorLabel')}</label>
            <input className="input-field" type="text" value={form.author} onChange={(e) => set('author', e.target.value)} />
            <div className="field-help">{mots('config.signatureAide')}</div>
          </div>
        </section>}
      </div>
    </>
  )
}

type Partie = 'cherche' | 'ecrit' | 'cv' | 'compte'

/**
 * « Enregistré », discret, près de la partie touchée (issue #174), au lieu
 * d'une notification à chaque clic. S'efface seul ; un nouveau clic relance
 * le délai.
 */
function useEnregistre(): [Partie | null, (p: Partie) => void] {
  const [partie, setPartie] = useState<Partie | null>(null)
  const minuterie = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(() => () => clearTimeout(minuterie.current), [])
  const marquer = (p: Partie) => {
    clearTimeout(minuterie.current)
    setPartie(p)
    minuterie.current = setTimeout(() => setPartie(null), 2500)
  }
  return [partie, marquer]
}

function Enregistre({ on }: { on: boolean }) {
  const t = useT()
  // Toujours dans le DOM, pour qu'un lecteur d'écran annonce le changement.
  return (
    <span className={`cfg-ok${on ? ' on' : ''}`} role="status" aria-live="polite">
      {on && <><Check size={13} strokeWidth={3} />{t('mails.saved')}</>}
    </span>
  )
}

/**
 * Les Réglages de Radar, en quatre parties (issue #173) : ce que je cherche,
 * quand Radar m'écrit, mes CV adaptés, mon compte. Tout s'enregistre au clic :
 * un rythme choisi doit valoir tout de suite, c'est lui que la page de
 * désabonnement promet de retrouver ici. Une phrase d'aide par partie au plus.
 */
function ReglagesRadar({ cv, enregistre, marquer }: { cv: React.ReactNode; enregistre: Partie | null; marquer: (p: Partie) => void }) {
  const t = useT()
  const toast = useToast()
  const etat = useData(() => get<EtatRythme>('/api/radar/rythme'), [])
  const e = etat.data
  // Retour du lien de confirmation d'adresse (`#/config?adresse=ok|ko`).
  useEffect(() => {
    const r = parseQuery(window.location.hash).get('adresse')
    if (r) toast(r === 'ok' ? t('adresse.confirmee') : t('adresse.lienInvalide'), r === 'ok' ? undefined : 'err')
  }, [])

  async function envoyer(corps: { rythme?: Rythme; courriers?: boolean; pause?: number; regions?: Region[]; mails?: ChangementMails; exclusions?: Exclusions }, partie: Partie) {
    try {
      await post('/api/radar/rythme', corps)
      marquer(partie)
      etat.reload()
    } catch (err: any) {
      toast(err?.message ?? String(err), 'err')
    }
  }

  // `?partie=compte` ou `?partie=ecrit` : l'aide mène droit à la partie
  // qui répond (« Je ne reçois pas les mails », « Je pars en vacances »).
  const partie = parseQuery(window.location.hash).get('partie')
  useEffect(() => {
    if (e && (partie === 'compte' || partie === 'ecrit')) document.getElementById(`reglages-${partie}`)?.scrollIntoView({ block: 'start' })
  }, [Boolean(e), partie])

  if (!e) return <Loading label={t('common.loading')} />

  return (
    <>
      <section className="panel">
        <h4><PastilleTitre h={150}><Search size={16} /></PastilleTitre>{t('reglages.cherche.titre')}<Enregistre on={enregistre === 'cherche'} /></h4>
        <p className="field-help">{t('reglages.cherche.aide')}</p>
        <div className="cfg-sous">
          <b>{t('reglages.regions')}</b>
          <ChoixRegions choisies={e.regions} onChange={(r) => envoyer({ regions: r }, 'cherche')} />
        </div>
        {e.exclusions && <ChoixExclusions e={e.exclusions} onChange={(x) => envoyer({ exclusions: x }, 'cherche')} />}
      </section>

      <section className="panel" id="reglages-ecrit">
        <h4><PastilleTitre h={215}><Mail size={16} /></PastilleTitre>{t('reglages.ecrit.titre')}<Enregistre on={enregistre === 'ecrit'} /></h4>
        {e.coupes ? (
          <p className="field-help">{t('reglages.mails.coupesIci')}</p>
        ) : (
          <>
            <p className="field-help">{e.rythme === null ? t('rythme.perso', { n: e.seuil }) : t('rythme.note')}</p>
            <ChoixRythme etat={e} choisi={e.rythme} onChoisir={(r) => envoyer({ rythme: r }, 'ecrit')} />
            {e.mails && <ChoixMails r={e.mails} onChange={(m) => envoyer({ mails: m }, 'ecrit')} />}
          </>
        )}
        {e.mails && <ChoixAvance r={e.mails} seuil={e.seuil} onChange={(m) => envoyer({ mails: m }, 'ecrit')} />}
      </section>

      {cv}

      <section className="panel" id="reglages-compte">
        <h4><PastilleTitre h={40}><UserRound size={16} /></PastilleTitre>{t('reglages.compte.titre')}<Enregistre on={enregistre === 'compte'} /></h4>
        {e.email && <ChoixAdresse etat={e} onChange={() => etat.reload()} />}
        <ChangerMotDePasseVolontaire onChange={() => marquer('compte')} />
        {/* Couper TOUS les mails n'est pas un réglage ordinaire : à part, en
            bas, loin du rythme qu'on règle tous les jours. */}
        <div className="cfg-sous cfg-separe">
          {e.coupes ? (
            <>
              <p className="cfg-explain avertissement">{t('reglages.mails.coupes')}</p>
              <div>
                <ActionButton variant="primary" onClick={() => envoyer({ courriers: true }, 'compte')}>
                  {t('reglages.mails.reactiver')}
                </ActionButton>
              </div>
            </>
          ) : (
            <>
              {/* La pause d'abord : couper pour de bon, on oublie de rallumer
                  (issue #175). Elle se lève seule à sa date. */}
              <b>{t('pause.titre')}</b>
              {e.pause ? (
                <span>
                  {t('pause.jusqua', { date: jourCourt(parseDate(e.pause) ?? new Date()) })}{' '}
                  <button type="button" className="linkbtn" onClick={() => envoyer({ pause: 0 }, 'compte')}>{t('accueil2.reprendre')}</button>
                </span>
              ) : (
                <div className="regions">
                  {[1, 2, 4].map((n) => (
                    <button key={n} type="button" className="region" onClick={() => envoyer({ pause: n }, 'compte')}>
                      {t(n === 1 ? 'pause.uneSemaine' : 'pause.semaines', { n })}
                    </button>
                  ))}
                </div>
              )}
              <div>
                <ActionButton onClick={() => envoyer({ courriers: false }, 'compte')}>
                  {t('reglages.mails.couper')}
                </ActionButton>
              </div>
            </>
          )}
        </div>
        <TelechargerDonnees />
        <SupprimerCompte />
      </section>
    </>
  )
}
